import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, Building2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import OverviewTab from '@/components/projects/OverviewTab';
import TasksTab from '@/components/projects/TasksTab';
import { CreateTaskForm } from '@/components/projects/CreateTaskForm';
import taskService from '@/services/task.service';
import projectService from '@/services/project.service';
import { timesheetService } from '@/services/timesheet.service';
import { useAuthStore } from '@/stores/authStore';

// Map API project to UI shape used by Overview/Tasks tabs
type HoursByDayEntry = { day: string; hours: number };

const mapApiProjectToUi = (api: any) => {
  const toTitleCase = (s?: string) => {
    if (!s) return '';
    const map: Record<string, string> = {
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
      ON_HOLD: 'On Hold',
      PLANNING: 'Planning',
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
    };
    return map[s] || s;
  };

  const team = Array.isArray(api.teamMembers)
    ? api.teamMembers.map((m: any) => ({
        _id: m._id,
        firstName: m.firstName,
        lastName: m.lastName,
        email: m.email,
        employeeId: m.employeeId,
        profilePicture: m.profilePicture,
      }))
    : [];

  // Extract status - handle both object and string formats
  let statusValue = api.status;
  if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
    statusValue = statusValue.name;
  }
  statusValue = toTitleCase(statusValue);

  return {
    id: api._id || api.id,
    _id: api._id || api.id,
    name: api.projectName,
    projectName: api.projectName,
    client: api.client,
    status: statusValue,
    priority: toTitleCase(api.priority),
    progress: api.progressPercentage ?? 0,
    description: api.projectDescription || '',
    startDate: api.startDate,
    endDate: api.endDate,
    createdDate: api.createdAt,
    projectManager: api.projectManager
      ? {
          _id: api.projectManager._id,
          firstName: api.projectManager.firstName,
          lastName: api.projectManager.lastName,
          email: api.projectManager.email,
          employeeId: api.projectManager.employeeId,
          profilePicture: api.projectManager.profilePicture,
        }
      : undefined,
    team,
    tags: api.tags || [],
    totalLoggedHours: 0,
    hoursByDay: [],
    tasks: { total: 0, notStarted: 0, inProgress: 0, testing: 0, readyForProduction: 0, onHold: 0, complete: 0 },
  } as any;
};

const getStatusColor = (status: string | any) => {
  // Extract status name if it's an object
  let statusName = status;
  if (status && typeof status === 'object' && 'name' in status) {
    statusName = status.name;
  }
  
  // Normalize status to uppercase for comparison
  const statusUpper = String(statusName || '').toUpperCase();
  
  switch (statusUpper) {
    case 'IN_PROGRESS':
    case 'IN PROGRESS':
    case 'ACTIVE':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'COMPLETED':
    case 'COMPLETE':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'ON_HOLD':
    case 'ON HOLD':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'PLANNING':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'TESTING':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'CANCELLED':
    case 'CANCELED':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasSubModulePermission } = useAuthStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState<any>((location.state as any)?.project || null);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0);

  useEffect(() => {
    // Always fetch latest data from API using the route id; prefilled state is used only as initial UI while loading
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const apiProject = await projectService.getProjectById(id);
        if (!cancelled) setProject(mapApiProjectToUi(apiProject));
      } catch (e) {
        // Keep whatever state we had; page already renders a skeleton
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  // If navigated with a specific task to open, ensure Tasks tab is active so the dialog can mount
  useEffect(() => {
    const openTaskId = (location.state as any)?.openTaskId;
    if (openTaskId) {
      setActiveTab('tasks');
    }
  }, [location.state]);

  

  // Stable projectId for effects
  const stableProjectId = useMemo(() => (project?._id || project?.id || id) as string | undefined, [project?._id, project?.id, id]);

  // Calculate total logged hours across all tasks in this project
  useEffect(() => {
    const projectId = stableProjectId;
    if (!projectId) return;

    let cancelled = false;
    const calculate = async () => {
      try {
        // Fetch tasks for the project
        const tasks = await taskService.getTasksByProject(projectId);
        if (!Array.isArray(tasks) || tasks.length === 0) {
          // Initialize empty hoursByDay for past 7 days
          const today = new Date();
          const hoursByDay: HoursByDayEntry[] = [];
          for (let i = 6; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            hoursByDay.push({
              day: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              hours: 0
            });
          }
          if (!cancelled) setProject((prev: any) => ({ 
            ...prev, 
            totalLoggedHours: 0,
            hoursByDay: hoursByDay
          }));
          return;
        }

        // Fetch timesheets for each task and sum durations
        const entriesByTask = await Promise.all(
          tasks.map(async (t: any) => {
            try {
              const entries = await timesheetService.getTimesheetEntriesByTask(t._id || t.id);
              return entries;
            } catch {
              return [] as any[];
            }
          })
        );

        const allEntries = entriesByTask.flat();
        
        const totalSeconds = allEntries.reduce((acc: number, e: any) => {
          if (typeof e?.duration === 'number' && e.duration > 0) return acc + e.duration;
          if (typeof e?.timeSpentMs === 'number' && e.timeSpentMs > 0) return acc + Math.floor(e.timeSpentMs / 1000);
          // Fallback: compute from timestamps if available
          if (e?.startTime && e?.endTime) {
            const diff = Math.max(0, new Date(e.endTime).getTime() - new Date(e.startTime).getTime());
            return acc + Math.floor(diff / 1000);
          }
          return acc;
        }, 0);

        const totalHours = Number((totalSeconds / 3600).toFixed(2));
        
        // Calculate hours by day for the past 7 days
        const hoursByDayMap = new Map<string, number>();
        
        // Initialize last 7 days with 0 hours
        const today = new Date();
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          hoursByDayMap.set(dayKey, 0);
        }
        
        // Group entries by day and sum hours
        allEntries.forEach((e: any) => {
          let seconds = 0;
          if (typeof e?.duration === 'number' && e.duration > 0) {
            seconds = e.duration;
          } else if (typeof e?.timeSpentMs === 'number' && e.timeSpentMs > 0) {
            seconds = Math.floor(e.timeSpentMs / 1000);
          } else if (e?.startTime && e?.endTime) {
            const diff = Math.max(0, new Date(e.endTime).getTime() - new Date(e.startTime).getTime());
            seconds = Math.floor(diff / 1000);
          }
          
          if (seconds > 0) {
            const entryDate = e.startTime ? new Date(e.startTime) : (e.createdAt ? new Date(e.createdAt) : new Date());
            const dayKey = entryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const hours = seconds / 3600;
            const currentHours = hoursByDayMap.get(dayKey) || 0;
            hoursByDayMap.set(dayKey, currentHours + hours);
          }
        });
        
        // Convert map to array format expected by chart
        const hoursByDay: HoursByDayEntry[] = Array.from(hoursByDayMap.entries()).map(([day, hours]) => ({
          day,
          hours: Number(hours.toFixed(2))
        }));
        
        if (!cancelled) {
          setProject((prev: any) => ({ 
            ...prev, 
            totalLoggedHours: totalHours,
            hoursByDay: hoursByDay
          }));
        }
      } catch (err) {
        // Keep silent in UI; card will show 0
        // Initialize empty hoursByDay for past 7 days
        const today = new Date();
        const hoursByDay: HoursByDayEntry[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          hoursByDay.push({
            day: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            hours: 0
          });
        }
        if (!cancelled) setProject((prev: any) => ({ 
          ...prev, 
          totalLoggedHours: 0,
          hoursByDay: hoursByDay
        }));
      } finally {
        // no-op
      }
    };

    calculate();
    return () => { cancelled = true; };
    // Recalculate on tab switch or when tasks may refresh; avoid depending on full project to prevent loops
  }, [activeTab, taskRefreshTrigger, stableProjectId]);

  // Helper to get the correct project ID (handles both _id and id)
  const getProjectId = () => {
    return project?._id || project?.id || id;
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-sm text-muted-foreground">Loading project…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/projects')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Projects</span>
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{project.projectName || project.name}</h1>
                {/* <p className="text-sm text-gray-600 mt-1">
                  Project #{getProjectId()} • {project.client}
                </p> */}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge className={cn('px-3 py-1', getStatusColor(project.status))}>
                {typeof project.status === 'object' && project.status !== null && 'name' in project.status 
                  ? project.status.name 
                  : String(project.status || '')}
              </Badge>
              <Badge className={cn('px-3 py-1', getPriorityColor(project.priority))}>
                {project.priority} Priority
              </Badge>
              {hasSubModulePermission('project_management', 'task', 'create') && (
                <Button 
                  onClick={() => setIsCreateTaskOpen(true)}
                  className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create New Task</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:grid-cols-2">
            <TabsTrigger value="overview" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Tasks</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <OverviewTab project={project} />
          </TabsContent>

          <TabsContent value="tasks" className="space-y-6">
            <TasksTab 
              project={{ ...project, id: getProjectId() }} 
              refreshTrigger={taskRefreshTrigger}
              initialOpenTaskId={(location.state as any)?.openTaskId}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Task Dialog */}
      <CreateTaskForm
        open={isCreateTaskOpen}
        onOpenChange={setIsCreateTaskOpen}
        projectId={getProjectId()}
        onTaskCreated={() => {
          // Handle task creation success
          setIsCreateTaskOpen(false);
          // Trigger task list refresh
          setTaskRefreshTrigger(prev => prev + 1);
        }}
      />
    </div>
  );
}